import { c as _c } from "react/compiler-runtime";
import { HotelCardPropertiesType, HotelInfo } from "@website/accommodation-ui/hotel-card-new";
import { starsHotelImage } from "@website/ui/stars-hotel-image";
import { getImage } from "@website/utils/get-image";
import { numberConvert } from "@website/utils/number-convert";
import { numberSeparator } from "@website/utils/number-separator";
type SchemaHotelListPagePropertiesType = {
  hotels: Array<HotelInfo>;
  locale?: string;
  dateFrom?: unknown;
  dateTo?: unknown;
  roomCapacity?: unknown;
  size?: HotelCardPropertiesType["size"];
  showCarousel?: HotelCardPropertiesType["showCarousel"];
};
const stringifySchema = (schema: unknown) => JSON.stringify(schema).replaceAll("<", String.raw`\u003c`);
const getSchemaImage = ({
  src,
  stars,
  size
}: {
  src?: string;
  stars: number;
  size?: HotelCardPropertiesType["size"];
}) => getImage({
  width: size === "xSmall" ? 200 : 350,
  height: size === "xSmall" ? 200 : 350,
  src: src ?? ""
}) || starsHotelImage(stars).src;
const getHotelImages = ({
  hotel,
  size,
  showCarousel
}: {
  hotel: HotelInfo;
  size?: HotelCardPropertiesType["size"];
  showCarousel?: HotelCardPropertiesType["showCarousel"];
}) => {
  const stars = hotel.hotelDetail?.stars ?? 1;
  if (hotel.gallery && showCarousel !== false && hotel.gallery.length > 1) {
    return [...new Set([...(hotel.gallery.length >= 5 ? hotel.gallery.slice(0, 5) : hotel.gallery), hotel.gallery.at(-1)].map(image => getSchemaImage({
      src: image?.media,
      stars,
      size
    })))];
  }
  return [getSchemaImage({
    src: hotel.thumbnail?.url,
    stars,
    size
  })];
};
const getHotelPriceRange = ({
  hotel,
  locale,
  dateFrom,
  dateTo,
  roomCapacity
}: Pick<SchemaHotelListPagePropertiesType, "locale" | "dateFrom" | "dateTo" | "roomCapacity"> & {
  hotel: HotelInfo;
}) => {
  const {
    price,
    priceOff = 0,
    basePriceOff
  } = hotel.pricing;
  const isVilla = process.env.NEXT_PUBLIC_APP_NAME === "villa";
  const hasDateAndPassengers = Boolean(dateFrom && dateTo && roomCapacity && Number(roomCapacity) > 0);
  const discountedPrice = priceOff && price > priceOff ? priceOff : price;
  const effectivePrice = isVilla ? hasDateAndPassengers ? discountedPrice : basePriceOff ?? discountedPrice : discountedPrice;
  if (!price) return "ناموجود";
  return `${numberConvert(numberSeparator(effectivePrice), {
    locale: locale || "fa"
  })} تومان`;
};
const getHotelAddress = (hotel: HotelInfo) => ({
  "@type": "PostalAddress",
  streetAddress: hotel.hotelDetail?.address ?? "",
  addressLocality: hotel.hotelDetail?.cityTitle ?? ""
});
export const SchemaHotelListPage = t0 => {
  const $ = _c(18);
  const {
    hotels,
    locale,
    dateFrom,
    dateTo,
    roomCapacity,
    size,
    showCarousel
  } = t0;
  let t1;
  let t2;
  if ($[0] !== dateFrom || $[1] !== dateTo || $[2] !== hotels || $[3] !== locale || $[4] !== roomCapacity || $[5] !== showCarousel || $[6] !== size) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      let t3;
      if ($[9] !== dateFrom || $[10] !== dateTo || $[11] !== locale || $[12] !== roomCapacity || $[13] !== showCarousel || $[14] !== size) {
        t3 = (hotel_0, index) => {
          const images = getHotelImages({
            hotel: hotel_0,
            size,
            showCarousel
          });
          const {
            isInternational
          } = hotel_0;
          return {
            "@type": "ListItem",
            position: index + 1,
            item: {
              "@type": "Hotel",
              image: images.length > 1 ? images : images[0],
              name: numberConvert(hotel_0.hotelDetail?.hotelTitle ?? "", {
                locale: isInternational ? "en" : locale || "fa"
              }),
              ...(hotel_0.hotelDetail?.stars > 0 ? {
                starRating: {
                  "@type": "Rating",
                  ratingValue: hotel_0.hotelDetail.stars
                }
              } : {}),
              address: getHotelAddress(hotel_0),
              priceRange: getHotelPriceRange({
                hotel: hotel_0,
                locale,
                dateFrom,
                dateTo,
                roomCapacity
              })
            }
          };
        };
        $[9] = dateFrom;
        $[10] = dateTo;
        $[11] = locale;
        $[12] = roomCapacity;
        $[13] = showCarousel;
        $[14] = size;
        $[15] = t3;
      } else {
        t3 = $[15];
      }
      const itemListElement = hotels.filter(_temp).map(t3);
      if (itemListElement.length === 0) {
        t2 = null;
        break bb0;
      }
      t1 = stringifySchema({
        "@context": "https://schema.org/",
        "@type": "ItemList",
        itemListElement
      });
    }
    $[0] = dateFrom;
    $[1] = dateTo;
    $[2] = hotels;
    $[3] = locale;
    $[4] = roomCapacity;
    $[5] = showCarousel;
    $[6] = size;
    $[7] = t1;
    $[8] = t2;
  } else {
    t1 = $[7];
    t2 = $[8];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  let t3;
  if ($[16] !== t1) {
    t3 = <script dangerouslySetInnerHTML={{
      __html: t1
    }} type="application/ld+json" />;
    $[16] = t1;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  return t3;
};
function _temp(hotel) {
  return hotel.hotelDetail?.isAvailable;
}